/**
 * Unified StockPool Configuration Component
 *
 * Replaces the scattered Scope and Stock Group configurations
 * with a single unified StockPool configuration.
 */

import { useState, useEffect } from "react";

export type StockPoolType = "all_a" | "single_index" | "multi_index" | "single_symbol" | "symbol_list";

export interface StockPoolParams {
  index_code?: string;
  index_codes?: string[];
  symbol?: string;
  symbols?: string[];
}

const STOCK_POOL_OPTIONS = [
  { value: "all_a", label: "全A股", description: "处理所有A股股票" },
  { value: "single_index", label: "单一股指", description: "处理特定指数成分股（如沪深300）" },
  { value: "multi_index", label: "多股指合并", description: "合并多个指数成分股" },
  { value: "single_symbol", label: "单一股票", description: "处理单个指定股票" },
  { value: "symbol_list", label: "股票列表", description: "处理指定的一组股票" },
] as const;

const INDEX_OPTIONS = [
  { value: "hs300", label: "沪深300" },
  { value: "zz500", label: "中证500" },
  { value: "zz1000", label: "中证1000" },
  { value: "zz2000", label: "中证2000" },
] as const;

interface StockPoolConfigProps {
  type: StockPoolType;
  params: StockPoolParams;
  onChange: (type: StockPoolType, params: StockPoolParams) => void;
  disabled?: boolean;
  className?: string;
}

export function StockPoolConfig({ type, params, onChange, disabled = false, className = "" }: StockPoolConfigProps) {
  const [localType, setLocalType] = useState<StockPoolType>(type || "all_a");
  const [localParams, setLocalParams] = useState<StockPoolParams>(params || {});
  const selectedIndexCode = String(localParams.index_code || "").trim();

  useEffect(() => {
    setLocalType(type || "all_a");
    setLocalParams(params || {});
  }, [type, params]);

  const handleTypeChange = (newType: StockPoolType) => {
    setLocalType(newType);
    // Reset params when type changes
    const defaultParams: Record<StockPoolType, StockPoolParams> = {
      all_a: {},
      single_index: { index_code: "hs300" },
      multi_index: { index_codes: ["hs300"] },
      single_symbol: { symbol: "" },
      symbol_list: { symbols: [] },
    };
    setLocalParams(defaultParams[newType]);
    onChange(newType, defaultParams[newType]);
  };

  const handleParamChange = (key: keyof StockPoolParams, value: unknown) => {
    const newParams = { ...localParams, [key]: value };
    setLocalParams(newParams);
    onChange(localType, newParams);
  };

  const normalizeSymbol = (raw: string): string => {
    let text = raw.trim().toUpperCase();
    if (!text) return "";
    if (!text.includes(".") && /^\d{6}$/.test(text)) {
      const suffix = text.startsWith("6") || text.startsWith("5") || text.startsWith("9") ? "SH" : "SZ";
      text = `${text}.${suffix}`;
    }
    return text;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* StockPool Type Selection */}
      <div>
        <label className="text-[#64748b] text-[10px] block mb-2">候选股票池类型</label>
        <div className="grid grid-cols-2 gap-2">
          {STOCK_POOL_OPTIONS.map((option) => (
            <button
              key={option.value}
              disabled={disabled}
              onClick={() => handleTypeChange(option.value)}
              className={`px-3 py-2 text-left text-xs rounded border transition-colors ${
                localType === option.value
                  ? "bg-[#0ea5e9]/20 border-[#0ea5e9] text-[#7dd3fc]"
                  : "bg-[#1a1d24] border-[var(--border)] text-[#94a3b8] hover:bg-[#252a33]"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <div className="font-medium">{option.label}</div>
              <div className="text-[9px] text-[#64748b] mt-0.5">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Dynamic Parameter Input */}
      <div className="bg-[#1a1d24] border border-[var(--border)] rounded p-3">
        {localType === "single_index" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">选择指数</label>
            <select
              value={selectedIndexCode}
              disabled={disabled}
              onChange={(e) => handleParamChange("index_code", e.target.value)}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            >
              <option value="">请选择指数</option>
              {INDEX_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {!selectedIndexCode && (
              <p className="mt-1 text-[10px] text-amber-300">
                当前模板未配置 index_code。保存时会被规范化为全A股或请手动选择指数。
              </p>
            )}
          </div>
        )}

        {localType === "multi_index" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">选择多个指数（逗号分隔）</label>
            <select
              multiple
              value={localParams.index_codes || ["hs300"]}
              disabled={disabled}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                handleParamChange("index_codes", selected);
              }}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
              size={4}
            >
              {INDEX_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {localType === "single_symbol" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">股票代码</label>
            <input
              type="text"
              value={localParams.symbol || ""}
              disabled={disabled}
              onChange={(e) => handleParamChange("symbol", normalizeSymbol(e.target.value))}
              placeholder="000001.SZ"
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            />
          </div>
        )}

        {localType === "symbol_list" && (
          <div>
            <label className="text-[#64748b] text-[10px] block mb-1">股票代码列表（逗号分隔）</label>
            <textarea
              value={(localParams.symbols || []).join(", ")}
              disabled={disabled}
              onChange={(e) => {
                const symbols = e.target.value
                  .split(/[,\n]/)
                  .map(s => normalizeSymbol(s.trim()))
                  .filter(Boolean);
                handleParamChange("symbols", symbols);
              }}
              placeholder="000001.SZ, 600000.SH, ..."
              rows={3}
              className="w-full px-2 py-1.5 bg-[#0f1115] border border-[var(--border)] rounded text-[#e2e8f0] text-xs"
            />
          </div>
        )}

        {localType === "all_a" && (
          <p className="text-[#64748b] text-[10px]">将处理所有A股股票（list_status=&apos;L&apos;）</p>
        )}
      </div>
    </div>
  );
}
